if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');

            if (!productElement) {
              window.location = opener.getAttribute('data-product-url');
              return;
            }

            this.preprocessHTML(productElement, opener);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

            this.setupMediaGalleryInteractions();
            this.setupVariantFormHandlers();

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            this.modalContent.querySelector('product-component')?.dispatchViewEvent?.();

            super.show(opener);

            // Re-initialize slider components after modal is open and rendered
            // so clientWidth / offsets calculation yields valid totals instead of NaN / -Infinity
            setTimeout(() => {
              this.modalContent.querySelectorAll('slider-component').forEach((sliderComp) => {
                if (typeof sliderComp.resetPages === 'function') {
                  sliderComp.resetPages();
                } else if (typeof sliderComp.initPages === 'function') {
                  sliderComp.initPages();
                }
              });
            }, 50);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      setupVariantFormHandlers() {
        console.log('[QuickAdd Debug] Initializing setupVariantFormHandlers');
        const productInfo = this.modalContent.querySelector('product-info');
        if (!productInfo) {
          console.log('[QuickAdd Debug] product-info element NOT found in modalContent');
          return;
        }

        const mainVariantInput = productInfo.querySelector('.product-variant-id, form .product-variant-id');
        const allIdInputs = productInfo.querySelectorAll('input[name="id"]');
        console.log('[QuickAdd Debug] Total input[name="id"] elements found:', allIdInputs.length, 'Main input:', mainVariantInput);

        allIdInputs.forEach((input, index) => {
          if (mainVariantInput && input !== mainVariantInput) {
            console.log(`[QuickAdd Debug] Disabling extra input[name="id"] #${index}:`, input);
            input.disabled = true;
            input.removeAttribute('name');
          }
        });

        const updateFormVariantId = () => {
          let variantId = null;

          // 1. Get currently selected radio / select option values
          const checkedInputs = Array.from(
            productInfo.querySelectorAll(
              'variant-radios input[type="radio"]:checked, variant-selects select option:checked, fieldset input[type="radio"]:checked'
            )
          );
          const selectedOptionValues = checkedInputs.map((inp) => inp.value?.trim()).filter(Boolean);
          console.log('[QuickAdd Debug] Currently selected option values:', selectedOptionValues);

          // 2. Read product.variants array from .product-variants-json script tag
          const variantsScript = productInfo.querySelector('script.product-variants-json');
          if (variantsScript) {
            try {
              const variantsList = JSON.parse(variantsScript.textContent);
              if (Array.isArray(variantsList) && variantsList.length > 0) {
                // Find matching variant from options array or title
                const matchedVariant = variantsList.find((v) => {
                  if (v.options && Array.isArray(v.options)) {
                    return v.options.every((optVal) => selectedOptionValues.includes(optVal.trim()));
                  }
                  return selectedOptionValues.includes(v.title?.trim()) || selectedOptionValues.includes(v.option1?.trim());
                });

                if (matchedVariant) {
                  variantId = matchedVariant.id;
                  console.log('[QuickAdd Debug] Matched variant from variants JSON:', matchedVariant.title, '-> ID:', variantId);
                  const mediaId = matchedVariant.featured_media?.id || matchedVariant.featured_image?.id;
                  const mediaSrc =
                    matchedVariant.featured_media?.src ||
                    matchedVariant.featured_media?.preview_image?.src ||
                    matchedVariant.featured_image?.src ||
                    (typeof matchedVariant.featured_image === 'string' ? matchedVariant.featured_image : null);

                  if (mediaId) {
                    const gallery = productInfo.querySelector('media-gallery') || this.modalContent.querySelector('media-gallery');
                    if (gallery && typeof gallery.setActiveMedia === 'function') {
                      gallery.setActiveMedia(mediaId, true);
                    }
                  }

                  if (mediaSrc) {
                    const activeMainImg = productInfo.querySelector('.product__media-list .product__media-item.is-active img, .product__media-list .product__media-item img');
                    if (activeMainImg) {
                      activeMainImg.src = mediaSrc;
                      activeMainImg.removeAttribute('srcset');
                    }

                    this.modalContent.querySelectorAll('.product-sticky-bar__media').forEach((img) => {
                      img.src = mediaSrc;
                      img.removeAttribute('srcset');
                    });
                  }
                } else if (selectedOptionValues.length > 0) {
                  // Fallback partial match
                  const partialMatch = variantsList.find((v) =>
                    selectedOptionValues.some((val) => v.title?.includes(val) || v.option1?.includes(val) || v.options?.includes(val))
                  );
                  if (partialMatch) {
                    variantId = partialMatch.id;
                    console.log('[QuickAdd Debug] Partial matched variant:', partialMatch.title, '-> ID:', variantId);
                  }
                }
              }
            } catch (e) {
              console.error('[QuickAdd Debug] Error parsing product-variants-json:', e);
            }
          }

          // 3. Fallback: If no matched variant from JSON, check script[data-selected-variant]
          if (!variantId) {
            const variantScript = productInfo.querySelector('script[data-selected-variant]');
            if (variantScript) {
              try {
                const variantData = JSON.parse(variantScript.textContent);
                if (variantData && variantData.id) {
                  variantId = variantData.id;
                  console.log('[QuickAdd Debug] Fallback to script[data-selected-variant]:', variantId);
                }
              } catch (e) {}
            }
          }

          if (variantId) {
            const targetInputs = productInfo.querySelectorAll('form input[name="id"], .product-variant-id');
            console.log('[QuickAdd Debug] Setting variantId =', variantId, 'on targetInputs:', targetInputs);
            targetInputs.forEach((input) => {
              input.value = variantId;
              input.disabled = false;
            });
          } else {
            console.warn('[QuickAdd Debug] Could not resolve a valid numeric variantId!');
          }
        };

        // Listen for change & click events on variant pickers
        productInfo.addEventListener('change', (e) => {
          console.log('[QuickAdd Debug] productInfo change event triggered by:', e.target);
          setTimeout(updateFormVariantId, 10);
        });

        productInfo.querySelectorAll('variant-selects input[type="radio"], variant-selects label, variant-selects select, variant-radios input[type="radio"], variant-radios label').forEach((el) => {
          el.addEventListener('click', () => {
            setTimeout(updateFormVariantId, 10);
          });
        });

        // Ensure variant ID is updated before form submission
        this.modalContent.querySelectorAll('form').forEach((form) => {
          form.addEventListener('submit', () => {
            console.log('[QuickAdd Debug] Form submit triggered. Sending input[name="id"]:', form.querySelector('input[name="id"]')?.value);
            updateFormVariantId();
          });
        });

        // Run once initially
        updateFormVariantId();
      }

      setupMediaGalleryInteractions() {
        const mediaGallery = this.modalContent.querySelector('media-gallery');
        if (!mediaGallery) return;

        const mediaList = mediaGallery.querySelector('.product__media-list');
        const thumbnailList = mediaGallery.querySelector('.thumbnail-list');
        if (!mediaList) return;

        const slides = Array.from(mediaList.querySelectorAll('.product__media-item'));
        if (slides.length === 0) return;

        const switchSlide = (targetSlide, targetThumbnailBtn = null) => {
          if (!targetSlide) return;

          slides.forEach((s) => s.classList.remove('is-active'));
          targetSlide.classList.add('is-active');

          mediaList.prepend(targetSlide);

          if (thumbnailList) {
            const thumbnailButtons = Array.from(thumbnailList.querySelectorAll('button'));
            thumbnailButtons.forEach((b) => b.removeAttribute('aria-current'));

            if (targetThumbnailBtn) {
              targetThumbnailBtn.setAttribute('aria-current', 'true');
            } else {
              const targetMediaId = targetSlide.dataset.mediaId;
              const matchingThumbLi = thumbnailList.querySelector(`[data-target="${targetMediaId}"]`);
              const matchingBtn = matchingThumbLi?.querySelector('button');
              if (matchingBtn) matchingBtn.setAttribute('aria-current', 'true');
            }
          }

          const activeImg = targetSlide?.querySelector('img') || targetThumbnailBtn?.querySelector('img');
          if (activeImg && activeImg.src) {
            const mainImg = targetSlide?.querySelector('.product__media img');
            if (mainImg && mainImg !== activeImg) {
              mainImg.src = activeImg.src;
              mainImg.removeAttribute('srcset');
            }
            this.modalContent.querySelectorAll('.product-sticky-bar__media').forEach((img) => {
              img.src = activeImg.src;
              img.removeAttribute('srcset');
            });
          }
        };

        if (thumbnailList) {
          const thumbnailItems = Array.from(thumbnailList.querySelectorAll('.thumbnail-list__item'));
          thumbnailItems.forEach((thumbItem, index) => {
            const btn = thumbItem.querySelector('button');
            if (!btn) return;

            btn.addEventListener('click', (e) => {
              e.preventDefault();
              e.stopPropagation();

              const targetMediaId = thumbItem.dataset.target;
              if (targetMediaId && typeof mediaGallery.setActiveMedia === 'function') {
                mediaGallery.setActiveMedia(targetMediaId, true);
              } else {
                let targetSlide = targetMediaId ? mediaList.querySelector(`[data-media-id="${targetMediaId}"]`) : null;
                if (!targetSlide && slides[index]) {
                  targetSlide = slides[index];
                }
                switchSlide(targetSlide, btn);
              }
            });
          });
        }

        const initialActive = mediaList.querySelector('.product__media-item.is-active') || slides[0];
        if (initialActive) {
          switchSlide(initialActive);
        }
      }

      preprocessHTML(productElement, opener) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
        this.addShortDescription(productElement, opener);
        this.enableMediaGallery(productElement);
      }

      addShortDescription(productElement, opener) {
        const productUrl = productElement.dataset.url || (opener ? opener.getAttribute('data-product-url') : '');
        const fullReadMoreUrl = productUrl ? `${window.location.origin}${productUrl}#product-description` : '#product-description';

        let shortDescEl = productElement.querySelector('.product__short-description');
        if (shortDescEl) {
          shortDescEl.classList.remove('quick-add-hidden');
          const readMoreLink = shortDescEl.querySelector('.product__short-description-read-more, a[href*="product-description"]');
          if (readMoreLink) {
            readMoreLink.setAttribute('href', fullReadMoreUrl);
            readMoreLink.removeAttribute('onclick');
          }
          return;
        }

        const fullDescEl = productElement.querySelector('.product__description, [id^="product-description"]');
        if (fullDescEl) {
          const text = fullDescEl.textContent.trim();
          if (text.length > 0) {
            const truncated = text.length > 200 ? text.substring(0, 200) + '...' : text;
            shortDescEl = document.createElement('div');
            shortDescEl.className = 'product__short-description rte';
            shortDescEl.innerHTML = `<p>${truncated} <a href="${fullReadMoreUrl}" class="product__short-description-read-more link underline">Read more</a></p>`;

            const insertTarget = productElement.querySelector('variant-selects, variant-radios, .product-form, [id^="price-"]');
            if (insertTarget && insertTarget.parentNode) {
              insertTarget.parentNode.insertBefore(shortDescEl, insertTarget.nextSibling);
            } else {
              const infoCol = productElement.querySelector('.product__info-container');
              if (infoCol) infoCol.appendChild(shortDescEl);
            }
          }
        }
      }

      enableMediaGallery(productElement) {
        const mediaGallery = productElement.querySelector('media-gallery');
        if (!mediaGallery) return;

        const mediaList = mediaGallery.querySelector('.product__media-list');
        const thumbnailList = mediaGallery.querySelector('.thumbnail-list');

        // Unhide thumbnail slider container
        const thumbnailSlider = mediaGallery.querySelector('.thumbnail-slider');
        if (thumbnailSlider) {
          thumbnailSlider.classList.remove('quick-add-hidden');
        }

        if (thumbnailList && mediaList) {
          thumbnailList.querySelectorAll('[data-target]').forEach((thumbnailLi) => {
            const button = thumbnailLi.querySelector('button');
            if (!button) return;

            button.addEventListener('click', (e) => {
              const targetMediaId = thumbnailLi.dataset.target;
              if (!targetMediaId) return;

              const targetSlide = mediaList.querySelector(`[data-media-id="${targetMediaId}"]`);
              if (targetSlide) {
                // Prepend slide so it becomes :first-child (main image)
                mediaList.prepend(targetSlide);

                // Update active classes
                mediaList.querySelectorAll('.product__media-item').forEach((slide) => slide.classList.remove('is-active'));
                targetSlide.classList.add('is-active');

                // Update aria-current on thumbnail buttons
                thumbnailList.querySelectorAll('button').forEach((b) => b.removeAttribute('aria-current'));
                button.setAttribute('aria-current', 'true');
              }
            });
          });
        }

        // Ensure the first media item has is-active if no slide is active
        const slides = mediaList ? mediaList.querySelectorAll('.product__media-item') : [];
        if (slides.length > 0 && !mediaList.querySelector('.product__media-item.is-active')) {
          slides[0].classList.add('is-active');
        }
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());

        // Strip executable <script> tags before injection to prevent document.write page wipes,
        // but PRESERVE <script type="application/json"> data blocks (e.g. data-selected-variant)
        // so Shopify's variant picker can resolve variant IDs cleanly.
        productElement.querySelectorAll('script:not([type="application/json"])').forEach((s) => s.remove());

        // Remove store-specific product-page sections that shouldn't appear in the
        // quick-add modal: "Add Something Extra" (.btn-demo) and
        // "Personalise Your Gift" (.btn-demo-per). Walk up to their .text-center
        // wrapper if present so the whole block is removed cleanly.
        ['.btn-demo', '.btn-demo-per'].forEach((sel) => {
          productElement.querySelectorAll(sel).forEach((el) => {
            const wrapper = el.closest('.text-center');
            (wrapper || el).remove();
          });
        });
      }


      preventDuplicatedIDs(productElement) {
        let sectionId = productElement.dataset.originalSection || productElement.dataset.section;
        if (!sectionId) return;

        // Ensure we always use the clean section ID without double "quickadd-"
        sectionId = sectionId.replace(/^quickadd-/, '');

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;

        if (productElement.innerHTML.includes(oldId)) {
          productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        }

        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId) && !attribute.value.includes(newId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.section = newId;
        productElement.dataset.originalSection = oldId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
